export type PomodoroPhase = 'focus' | 'break';

export interface PomodoroSession {
  todoId: string;
  title: string;
  phase: PomodoroPhase;
  /** Epoch milliseconds when the current phase ends */
  endsAt: number;
}

export const FOCUS_SECONDS = 25 * 60;
export const BREAK_SECONDS = 5 * 60;

const STORAGE_KEY = 'riselio:pomodoro';

export const startSession = (todoId: string, title: string, now: number = Date.now()): PomodoroSession => ({
  todoId,
  title,
  phase: 'focus',
  endsAt: now + FOCUS_SECONDS * 1000,
});

export const remainingSeconds = (session: PomodoroSession, now: number = Date.now()): number =>
  Math.max(0, Math.ceil((session.endsAt - now) / 1000));

export interface AdvanceResult {
  /** The session to keep running, or null when the cycle is over */
  session: PomodoroSession | null;
  /** True when a focus phase just ended and should be counted */
  focusCompleted: boolean;
}

/** Moves a finished phase on: focus -> break (counted), break -> done. Unfinished phases are returned as is. */
export const advance = (session: PomodoroSession, now: number = Date.now()): AdvanceResult => {
  if (remainingSeconds(session, now) > 0) return { session, focusCompleted: false };
  if (session.phase === 'focus') {
    return { session: { ...session, phase: 'break', endsAt: now + BREAK_SECONDS * 1000 }, focusCompleted: true };
  }
  return { session: null, focusCompleted: false };
};

export const loadSession = (): PomodoroSession | null => {
  try {
    const raw = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (
      raw && typeof raw.todoId === 'string' && typeof raw.title === 'string'
      && (raw.phase === 'focus' || raw.phase === 'break') && Number.isFinite(raw.endsAt)
    ) {
      return raw as PomodoroSession;
    }
  } catch {
    // Storage can be blocked or hold junk; start without a session.
  }
  return null;
};

export const saveSession = (session: PomodoroSession | null): void => {
  try {
    if (session) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Persistence is a convenience only.
  }
};
